import Yup from '~/util/yup';
import { documentVerbalOrderReceivedOptions, validateOrderOptions, type OrderViewModel } from '@generated';
import { FormModal } from '~/shared/forms/FormModal';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { DateTimeFormField } from '~/shared/forms/controls/fields/date-fields/DateTimeFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { getInvalidateOrderQueryKeys } from './util';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Prompt } from '~/shared/components/Prompt';

const now = new Date();

export function SubmitVerbalOrderFormModal({
	order,
	onSuccess,
	closeModal
}: {
	order: OrderViewModel;
	onSuccess: () => void;
	closeModal: () => void;
}) {
	const { data: validation } = useSuspenseQuery({
		...validateOrderOptions(order.id),
		staleTime: 1,
		gcTime: 1
	});

	if (validation.length > 0) {
		return (
			<Prompt
				intent="confirm"
				title="Cannot Document Verbal Order"
				text="This order cannot be documented as a verbal order because it is not in a valid state. Please review the validation errors below."
				onAction={closeModal}
			>
				<div className="mt-4">
					<ul className="list-disc pl-5 text-sm text-slate-500">
						{validation.map(error => (
							<li key={error.error}>{error.error}</li>
						))}
					</ul>
				</div>
			</Prompt>
		);
	}

	return (
		<FormModal
			title="Document Verbal Order"
			initialValues={{
				stayId: order.stayId,
				orderId: order.id,
				verbalOrderReceivedAt: now,
				verbalOrderReceivedFrom: null,
				orderReadBackAndVerified: false
			}}
			validationSchema={Yup.object().shape({
				verbalOrderReceivedAt: Yup.date().nullable().label('Verbal Order Received At').required(),
				verbalOrderReceivedFrom: Yup.string().nullable().label('Verbal Order Received From').required(),
				orderReadBackAndVerified: Yup.boolean()
					.label('Order Read Back and Verified')
					.oneOf([true], 'Order Read Back and Verified must be true when documenting a verbal order')
			})}
			saveMutation={{
				...documentVerbalOrderReceivedOptions,
				invalidateQueryKeys: getInvalidateOrderQueryKeys(order)
			}}
			onSuccess={onSuccess}
			closeModal={closeModal}
		>
			<div className="grid grid-cols-1 gap-6">
				<TextFormField name="verbalOrderReceivedFrom" />
				<DateTimeFormField name="verbalOrderReceivedAt" granularity="minute" />
				<CheckboxFormField name="orderReadBackAndVerified" hideLabel />
			</div>
		</FormModal>
	);
}
